import React, { useState } from 'react'

import { Moon, Sun } from 'lucide-react'

import { useTheme } from '@/shared/contexts/ThemeContext'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import FlatIconButton from '@/shared/ui/FlatIconButton'

interface ThemeToggleProps {
  /** Icon size in px (default: 16 for glass, 18 for flat). Overrides auto-detection. */
  iconSize?: number
  /** Force a variant; auto-detects via useIsMobile when omitted. */
  flat?: boolean
  /** Additional styles to merge */
  style?: React.CSSProperties
}

/**
 * Theme toggle button — auto-adapts to viewport:
 * - **≤1023px (mobile)**: transparent FlatIconButton (matching frontend MobileTopBar)
 * - **≥1024px (desktop)**: 36×36 frosted glass squircle with inner gradient border stroke
 *
 * 关联文档文件：FlatIconButton.tsx, ThemeContext.tsx, useIsMobile.ts
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({ iconSize, flat: forceFlat, style }) => {
  const { theme, toggleTheme } = useTheme()
  const isMobile = useIsMobile()
  const [hovered, setHovered] = useState(false)
  const ariaLabel = theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'

  const flat = forceFlat ?? isMobile
  const resolvedIconSize = iconSize ?? (flat ? 20 : 16)

  if (flat) {
    return (
      <FlatIconButton onClick={toggleTheme} aria-label={ariaLabel} style={style}>
        {theme === 'light' ? <Moon size={resolvedIconSize} /> : <Sun size={resolvedIconSize} />}
      </FlatIconButton>
    )
  }

  return (
    <button
      type="button"
      onClick={toggleTheme}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'var(--theme-toggle-desktop-size)',
        height: 'var(--theme-toggle-desktop-size)',
        borderRadius: 'var(--radius-lg)',
        backgroundColor: hovered ? 'var(--app-header-glass-bg-hover)' : 'var(--app-header-glass-bg)',
        backdropFilter: 'var(--theme-toggle-glass-blur)',
        WebkitBackdropFilter: 'var(--theme-toggle-glass-blur)',
        boxShadow: 'var(--app-header-glass-shadow)',
        color: 'var(--app-text-secondary)',
        transition: 'background-color 0.2s',
        flexShrink: 0,
        border: 'none',
        cursor: 'pointer',
        overflow: 'visible',
        ...style,
      }}
      className="active:opacity-70"
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {/* Inner gradient border stroke — matches frontend glassStrokeOverlayStyle */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: 'var(--theme-toggle-glass-inset)',
          borderRadius: 'var(--theme-toggle-glass-radius-inner)',
          border: 'var(--theme-toggle-glass-border) solid transparent',
          background:
            'linear-gradient(145deg, rgba(255,255,255,0.7) 0%, rgba(243,248,255,0) 34%, rgba(255,255,255,0.4) 68%, rgba(228,239,255,0.8) 100%)',
          opacity: 0.3,
          pointerEvents: 'none',
          WebkitMask: 'linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />
      <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
        {theme === 'light' ? <Moon size={resolvedIconSize} /> : <Sun size={resolvedIconSize} />}
      </span>
    </button>
  )
}

export default ThemeToggle
